import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { addCsrfHeader } from '../utils/csrfUtils';

export const useAddRecipeDislike = () => {
    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const addRecipeDislike = async (recipeId) => {
        setIsAdding(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch('/api/v1/recipe-dislike', {
                method: 'POST',
                headers,
                body: JSON.stringify({ recipe_id: recipeId }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add recipe to dislikes');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsAdding(false);
        }
    };

    return {
        addRecipeDislike,
        isAdding,
        error
    };
};

export const useDeleteRecipeDislike = () => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const deleteRecipeDislike = async (recipeId) => {
        setIsDeleting(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Add CSRF token and auth header
            const headers = await addCsrfHeader({
                'Authorization': `Bearer ${sessionToken}`
            });

            const response = await fetch(`/api/v1/recipe-dislike/recipe/${recipeId}`, {
                method: 'DELETE',
                headers
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to remove recipe from dislikes');
            }

            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        deleteRecipeDislike,
        isDeleting,
        error
    };
};

export const useGetRecipeDislikeStatus = () => {
    const [isChecking, setIsChecking] = useState(false);
    const [error, setError] = useState(null);
    const { sessionToken } = useAuth();

    const getRecipeDislikeStatus = async (recipeId) => {
        setIsChecking(true);
        setError(null);

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const headers = {
                'Authorization': `Bearer ${sessionToken}`
            };

            const response = await fetch(`/api/v1/recipe-dislike/recipe/${recipeId}`, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to check recipe dislike status');
            }

            const data = await response.json();
            return data.isDisliked;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsChecking(false);
        }
    };

    return {
        getRecipeDislikeStatus,
        isChecking,
        error
    };
};

export const useGetAllDislikedRecipes = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dislikedRecipes, setDislikedRecipes] = useState([]);
    const { sessionToken } = useAuth();

    const getAllDislikedRecipes = React.useCallback(async (sessionToken) => {
        if (!sessionToken) {
            setError('No session token provided');
            setDislikedRecipes([]);
            return [];
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/v1/recipe-dislike/my-dislikes', {
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            console.log('getAllDislikedRecipes (hook) status:', response.status);
            console.log('getAllDislikedRecipes (hook) statusText:', response.statusText);
            
            const data = await response.json();
            console.log('getAllDislikedRecipes (hook) data:', data);

            if (!response.ok) {
                throw new Error(data.error || 'Failed to fetch disliked recipes');
            }
            
            // Extract just the recipe_ids into an array of numbers
            const recipeIds = data.map(dislike => Number(dislike.recipe_id));
            console.log('Extracted disliked recipe IDs:', recipeIds);
            setDislikedRecipes(recipeIds);
            return recipeIds;

        } catch (err) {
            setError(err.message);
            setDislikedRecipes([]);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, []); // Empty dependency array since this function doesn't depend on any props or state

    return {
        getAllDislikedRecipes,
        dislikedRecipes,
        isLoading,
        error
    };
};

